/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DestroyRef, OnInit} from '@angular/core';
import {LookupService} from '../../common/service/lookup.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm, ReactiveFormsModule} from '@angular/forms';
import {equals, isDefined} from 'src/app/common/service/data-util';
import {PlanningApproval} from '../../common/models/planning-approval-model';
import {PlanningAuthority} from '../../common/models/planing-authority.model';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {NlrPatternDirective} from '../../common/directive/nlr-pattern.directive';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {PlanningAuthorityReference} from '../../common/models/planning-authority-reference.model';
import {NlrForbiddenValuesDirective} from '../../common/directive/nlr-forbidden-values.directive';
import {NlrSanitizeResourcePipe} from '../../common/pipe/nlr-sanitize-resource-pipe';
import {PermissionsService} from '../../common/service/permissions.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-planning-authority',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        NgClass,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        NlrPatternDirective,
        NlrFormFieldHasErrorPipe,
        NlrForbiddenValuesDirective,
        NlrSanitizeResourcePipe
    ],
    templateUrl: './planning-authority.component.html',
    styleUrl: './planning-authority.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class PlanningAuthorityComponent implements OnInit {
    title = 'Planning authority';

    planningAuthority: string = undefined;

    private planningApprovalLookups: PlanningApproval[] = undefined;

    private planningAuthorityLookups: PlanningAuthority[] = undefined;

    private planningAuthorityAtLastCheck: any;

    private planPurposeAtLastCheck: any;

    editViewInformationPermissions = ['EDIT_INFORMATION', 'VIEW_INFORMATION'];
    private planningApprovalRequiredFlag: string;

    constructor(
        private lookupService: LookupService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.initialiseLookups();
        this.eventBus
            .on('nlr:plan-model-updated:*')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                switch (meta.data) {
                    case 'planPurpose': {
                        this.onPlanPurposeChange();
                        break;
                    }
                    case 'model': {
                        this.planningAuthority = this.getModel().planningAuthority?.planningAuthority;
                        this.planningApprovalRequiredFlag = this.getModel().planningAuthority?.planningApprovalRequired;
                        if (
                            this.getModel().planningAuthority?.referenceMandatory &&
                            (this.getModel().planningAuthorityReferences == undefined || this.getModel().planningAuthorityReferences.length === 0)
                        ) {
                            this.addPlanningAuthorityReference();
                        }
                        break;
                    }
                }
            });
    }

    get planningApprovalRequired() {
        return this.planningApprovalRequiredFlag;
    }

    set planningApprovalRequired(value: any) {
        this.planningApprovalRequiredFlag = value;
    }
    getModel() {
        return this.planModelService.getModel();
    }

    getPlanningAuthorityReferences() {
        return this.getModel().planningAuthorityReferences;
    }

    getForbiddenReferenceNumbers(planningAuthorityReference: PlanningAuthorityReference) {
        return this.getPlanningAuthorityReferences()
            .filter(p => p !== planningAuthorityReference)
            .map(p => p.referenceNumber)
            .filter((v, i, a) => v && a.indexOf(v) === i);
    }

    private initialiseLookups() {
        this.planningApprovalLookups = this.lookupService.getLookups('planningApproval').sort((a, b) => a.uiOptionOrder - b.uiOptionOrder);
        this.planningAuthorityLookups = this.lookupService.getLookups('planningAuthority');
    }

    getPlanningApprovals() {
        const {tenureType, planPurpose, planType} = this.getModel();
        if (this.planningApprovalLookups === undefined) {
            return undefined;
        }
        return this.planningApprovalLookups.filter(
            v => planPurpose && v.planPurpose === planPurpose.name && v.tenureType === tenureType.name && v.planType === planType.name
        );
    }

    hasPlanningApprovals() {
        return isDefined(this.getPlanningApprovals()) && this.getPlanningApprovals().length > 0;
    }

    getPlanningApprovalRequiredValues() {
        return this.getPlanningApprovals()
            .map(v => v.planningApprovalRequired)
            .filter((v, i, a) => a.indexOf(v) === i);
    }

    getPlanningAuthorityValues() {
        return this.getPlanningApprovals()
            .filter(v => v.planningAuthority && v.planningApprovalRequired === this.planningApprovalRequiredFlag)
            .map(v => v.planningAuthority);
    }

    getPlanningAuthorityDisplayName(option) {
        return this.planningAuthorityLookups.find(l => l.id === option).name;
    }

    getInformationMessage() {
        return (
            this.planningApprovalRequiredFlag &&
            this.getPlanningApprovals()
                .filter(
                    v =>
                        v.planningApprovalRequired === this.planningApprovalRequiredFlag &&
                        (!this.planningAuthority || v.planningAuthority === this.planningAuthority)
                )
                .map(v => v.informationMessage)[0]
        );
    }

    addPlanningAuthorityReference() {
        console.debug(`${this.title} controller addPlanningAuthorityReference`);
        if (!this.getPlanningAuthorityReferences()) {
            this.getModel().planningAuthorityReferences = [{referenceNumber: ''}];
        } else {
            this.getModel().planningAuthorityReferences.push({referenceNumber: ''});
        }
    }

    removePlanningAuthorityReference(i: number) {
        console.debug(`${this.title} controller removePlanningAuthorityReference`);
        this.getPlanningAuthorityReferences().splice(i, 1);
    }

    planningApprovalRequiredChanged() {
        console.debug(`${this.title} controller planningApprovalChanged`);
        this.initPlanningAuthority();
    }

    initPlanningAuthority() {
        const planModel = this.getModel();
        const filteredPlanningApprovals = this.getPlanningApprovals().filter(v => v.planningApprovalRequired === this.planningApprovalRequiredFlag);
        if (filteredPlanningApprovals.length === 1) {
            // planningAuthority set the default value if there is only one value
            const defaultPlanningApproval = filteredPlanningApprovals[0];
            this.planningAuthority = defaultPlanningApproval.planningAuthority;
            planModel.planningAuthority = structuredClone(defaultPlanningApproval);
            if (
                planModel.planningAuthority.referenceMandatory &&
                (planModel.planningAuthorityReferences == undefined || planModel.planningAuthorityReferences.length === 0)
            ) {
                this.addPlanningAuthorityReference();
            }
        } else {
            const planningApprovalsWithoutAuthority = filteredPlanningApprovals.filter(v => !v.planningAuthority);
            if (planningApprovalsWithoutAuthority && planningApprovalsWithoutAuthority[0]) {
                this.planningAuthority = planningApprovalsWithoutAuthority[0].planningAuthority;
                planModel.planningAuthority = structuredClone(planningApprovalsWithoutAuthority[0]);
                if (
                    planModel.planningAuthority.referenceMandatory &&
                    (planModel.planningAuthorityReferences == undefined || planModel.planningAuthorityReferences.length === 0)
                ) {
                    this.addPlanningAuthorityReference();
                }
            }
        }

        if (this.planningApprovalRequiredFlag) {
            const selectedPlanningApproval = this.getPlanningApprovals().find(
                v => v.planningAuthority === this.planningAuthority && v.planningApprovalRequired === this.planningApprovalRequiredFlag
            );
            if (selectedPlanningApproval && planModel.planningAuthority !== selectedPlanningApproval) {
                planModel.planningAuthority = structuredClone(selectedPlanningApproval);
                this.planningAuthority = selectedPlanningApproval.planningAuthority;
                this.planningApprovalRequiredFlag = planModel.planningAuthority.planningApprovalRequired;
                planModel.planningAuthorityReferences = [];
                if (planModel.planningAuthority.referenceMandatory) {
                    this.addPlanningAuthorityReference();
                }
            }
        }
    }

    onPlanningAuthorityChange() {
        this.initPlanningAuthority();
    }

    onPlanPurposeChange() {
        const planModel = this.getModel();
        const planPurpose = planModel.planPurpose;

        if (planPurpose && this.hasPlanningApprovals()) {
            if (planModel.planningAuthority) {
                if (
                    planPurpose &&
                    this.planningAuthorityAtLastCheck &&
                    !equals(planPurpose, this.planPurposeAtLastCheck) &&
                    !equals(planModel.planningAuthority, this.planningAuthorityAtLastCheck)
                ) {
                    planModel.planningAuthority = undefined;
                    planModel.planningAuthorityReferences.length = 0;

                    this.planningAuthority = undefined;
                } else {
                    this.planningAuthority = planModel.planningAuthority.planningAuthority;
                }
            }

            if (!planModel.planningAuthority) {
                this.initPlanningAuthority();
            }
        } else if (planModel.tenureType) {
            // Check tenureType is present to ensure planning authority related attributes are not cleared during initial plan load
            if (planModel.planningAuthority) {
                planModel.planningAuthority = undefined;
            }
            planModel.planningAuthorityReferences = [];
            this.planningAuthority = undefined;
        }
    }

    //TODO ????
    serverBackedClientValidationEnabled() {
        // $rootScope.serverBackedClientValidationEnabled = function() {
        //     if($state.current.name && $state.current.name === "plan") {
        //         let validationSources = $stateParams.validation ? $stateParams.validation.split(",") : ["client"];
        //         return !validationSources.includes("server");
        //     }
        //     return true
        // };
        return true;
    }

    isReadonly() {
        return this.planModelService.readonly;
    }

    referencePattern = '^[^,]*$';

    getErrorList(errorObject) {
        Object.keys(errorObject).forEach(s => {
            console.log(s + ' ' + errorObject[s]);
        });
        return Object.keys(errorObject);
    }

    isActive(option: string) {
        return this.getPlanningApprovals().filter(value => value.planningApprovalRequired === option)[0].active;
    }

    isDisabled() {
        return this.planModelService.readonly || this.permissionsService.isDisabledByPermissions(this.editViewInformationPermissions);
    }
}
